/**
 * Event Helper Utilities
 *
 * Centralized utilities for handling access control events across the application.
 * Extracted from AccessControl.tsx to ensure consistent event handling everywhere.
 */

export interface AccessEvent {
  id: string;
  person_id?: string;
  person_name: string;
  is_authorized: boolean;
  is_registered?: boolean;
  event_type: string;
  event_timestamp: string;
  zone_name: string;
  device_name?: string;
  customer_name?: string;
  identity_photo_url?: string;
  face_photo_url?: string;
  action?: 'entry' | 'exit' | 'passage';
  direction?: string;
  not_registered_in_system?: boolean;
  triggered_by_name?: string;
  triggered_by_email?: string;
  triggered_by_user_id?: string;
  authorized?: boolean;
  vehicle_plate?: string;
}

/**
 * Identifies if an event type is a system event (not person-related)
 */
export const isSystemEvent = (eventType?: string): boolean => {
  const systemEventTypes = [
    'door_opened',
    'door_closed',
    'exit_button_pressed',
    'alarm_triggered',
    'door_open_timeout',
    'device_online',
    'device_offline',
    'system_event'
  ];
  return eventType ? systemEventTypes.includes(eventType) : false;
};

/**
 * Translates event type codes to Portuguese labels
 */
export const getEventTypeLabel = (eventType: string): string => {
  const labels: Record<string, string> = {
    'authentication_success': 'Autenticação Bem-sucedida',
    'authentication_failed': 'Autenticação Falhou',
    'face_recognized': 'Face Reconhecida',
    'door_opened': 'Porta Aberta',
    'door_open_timeout': 'Alerta de Porta Aberta',
    'door_closed': 'Porta Fechada',
    'exit_button_pressed': 'Botão de Saída Pressionado',
    'alarm_triggered': 'Alarme Acionado',
    'system_event': 'Evento de Sistema',
    'remote_user': 'Abertura Remota',
    'remote_door_trigger': 'Abertura de Porta Remota',
    'rfid_access': 'Acesso RFID',
    'rfid_access_granted': 'Acesso RFID Concedido',
    'rfid_access_denied': 'Acesso RFID Negado',
    'access_granted': 'Acesso Concedido',
    'access_denied': 'Acesso Negado',
    'device_online': 'Dispositivo Online',
    'device_offline': 'Dispositivo Offline',
    'entry': 'Entrada',
    'exit': 'Saída',
    'denied': 'Negado',
    'manual_check_in': 'Entrada Manual',
    'manual_check_out': 'Saída Manual',
    'auto_check_in': 'Entrada Automática',
    'auto_check_out': 'Saída Automática',
    'lpr_access': 'Acesso LPR',
  };
  return labels[eventType] || eventType;
};

/**
 * Was this gate opened remotely — somebody pressing a button in the app or in the web — rather
 * than a person authenticating at the reader?
 *
 * TWO names for one thing: `remote_door_trigger` is what both producers write (condo-manager's
 * logRemoteDoorOpenEvent for app opens, iot-connect's door_handler for the ones it relays), and
 * `remote_user` is what some Hikvision devices report for the same event. Five places in the web
 * were testing for that pair by hand, so the pair lives here once.
 */
export const isRemoteDoorEvent = (eventType?: string): boolean =>
  eventType === 'remote_door_trigger' || eventType === 'remote_user';

export interface AccessHistoryBadge {
  /** 'manual' | 'authenticated' | 'remote' | 'automatic' — drives the badge colour too. */
  kind: 'manual' | 'authenticated' | 'remote' | 'automatic';
  label: string;
  tooltip: string;
}

/**
 * What a row of a person's HISTÓRICO actually is.
 *
 * The history is what gets read when somebody asks how a person got in, so the row has to say
 * the right thing: a remote opening used to read "Registro automático — o sistema detectou e
 * registrou esta movimentação automaticamente via dispositivo", when nothing was detected at all
 * and no face was at the reader. It is also why those rows have no DIREÇÃO: a remote open is not
 * a passage through a device.
 *
 * Returns null for rows that are not movements (an authorization, a revocation) — the tab shows
 * no badge for those.
 */
export const accessHistoryBadge = (
  event: { event_type?: string; details?: Record<string, any> | null }
): AccessHistoryBadge | null => {
  const eventType = event.event_type;

  if (eventType === 'authorization' || eventType === 'revoke') return null;

  if (eventType === 'manual_check_in' || eventType === 'manual_check_out') {
    return {
      kind: 'manual',
      label: 'Registro manual',
      tooltip: 'O porteiro ou operador registrou esta movimentação manualmente no sistema.',
    };
  }

  if (isRemoteDoorEvent(eventType)) {
    // How the open reached the gate: "direct" means the phone dialled the device on the
    // condomínio LAN (so the person was there), "cloud" means condo-manager relayed it (so they
    // could have been anywhere). Worth distinguishing in a history somebody audits.
    const method = typeof event.details?.trigger_method === 'string' ? event.details.trigger_method : '';
    const comoChegou =
      method === 'direct' ? ' O comando saiu do aparelho pela rede local do condomínio.'
        : method === 'cloud' ? ' O comando passou pelo servidor.'
          : '';
    return {
      kind: 'remote',
      label: 'Abertura remota',
      tooltip: `A pessoa abriu este acionador remotamente, pelo app ou pelo sistema — não houve autenticação no dispositivo.${comoChegou}`,
    };
  }

  if (eventType === 'authentication_success' || eventType === 'face_recognized') {
    return {
      kind: 'authenticated',
      label: 'Autenticação Bem-sucedida',
      tooltip: 'O dispositivo autenticou a pessoa com sucesso (facial, RFID, etc.).',
    };
  }

  return {
    kind: 'automatic',
    label: 'Registro automático',
    tooltip: 'O sistema detectou e registrou esta movimentação automaticamente via dispositivo.',
  };
};

/**
 * Returns badge configuration for an event action/type
 */
export const getActionBadge = (event: AccessEvent): { text: string; color: string } => {
  // Priority 1: Use action field if available
  if (event.action === 'entry') {
    return { text: 'Entrada', color: 'bg-green-100 text-green-800' };
  }
  if (event.action === 'exit') {
    return { text: 'Saída', color: 'bg-orange-100 text-orange-800' };
  }
  if (event.action === 'passage') {
    return { text: 'Passagem', color: 'bg-blue-100 text-blue-800' };
  }

  // Priority 2: Use direction field if available
  if (event.direction === 'entry') {
    return { text: 'Entrada', color: 'bg-green-100 text-green-800' };
  }
  if (event.direction === 'exit') {
    return { text: 'Saída', color: 'bg-orange-100 text-orange-800' };
  }

  // Priority 3: Map event_type to badge
  const badges: Record<string, { text: string; color: string }> = {
    'entry': { text: 'Entrada', color: 'bg-green-100 text-green-800' },
    'exit': { text: 'Saída', color: 'bg-orange-100 text-orange-800' },
    'authentication_success': { text: 'Autenticado', color: 'bg-blue-100 text-blue-700' },
    'face_recognized': { text: 'Face Reconhecida', color: 'bg-blue-100 text-blue-700' },
    'door_opened': { text: 'Porta Aberta', color: 'bg-yellow-100 text-yellow-700' },
    'door_closed': { text: 'Porta Fechada', color: 'bg-gray-100 text-gray-700' },
    'alarm_triggered': { text: 'Alarme', color: 'bg-red-100 text-red-700' },
    'device_online': { text: 'Online', color: 'bg-green-100 text-green-700' },
    'device_offline': { text: 'Offline', color: 'bg-red-100 text-red-700' },
    'access_granted': { text: 'Concedido', color: 'bg-green-100 text-green-700' },
    'access_denied': { text: 'Negado', color: 'bg-red-100 text-red-700' },
  };

  return badges[event.event_type] || { text: 'Evento', color: 'bg-gray-100 text-gray-700' };
};

/**
 * Determines the background color class for an event row
 */
export const getRowBackground = (event: AccessEvent): string => {
  // System events: Gray background
  if (isSystemEvent(event.event_type)) {
    return 'bg-gray-50 hover:bg-gray-100';
  }

  // LPR reads are neutral — an unregistered plate isn't an unauthorized access attempt
  if (event.event_type === 'lpr_access') {
    return 'hover:bg-gray-50 cursor-pointer';
  }

  // Unauthorized or non-registered person events: Red background
  const isUnauthorized = !event.is_authorized ||
                         !event.authorized ||
                         event.not_registered_in_system ||
                         (event.is_registered === false);

  if (isUnauthorized) {
    return 'bg-red-50 hover:bg-red-100 cursor-pointer';
  }

  // Default: White background with hover
  return 'hover:bg-gray-50 cursor-pointer';
};

/**
 * Checks if an event row should be clickable
 */
export const isEventClickable = (event: AccessEvent): boolean => {
  // System events are not clickable
  if (isSystemEvent(event.event_type)) {
    return false;
  }

  // Events with valid person_id are clickable
  return Boolean(event.person_id && event.person_id !== '00000000-0000-0000-0000-000000000000');
};

/**
 * Formats timestamp to relative time (e.g., "45min atrás")
 */
export const formatRelativeTime = (timestamp: string): string => {
  if (!timestamp) return '';

  const now = new Date();
  const eventTime = new Date(timestamp);
  const diffMs = now.getTime() - eventTime.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'agora';
  if (diffMins < 60) return `${diffMins}min atrás`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h atrás`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d atrás`;
};

/**
 * Formats timestamp to full date/time string (e.g., "15/01/2025 10:23")
 */
export const formatFullTime = (timestamp: string): string => {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/** Placeholder event-processor writes when a card read resolved to nobody. */
const UNREGISTERED_TAG_LABEL = 'Tag não cadastrada';

export interface UnregisteredTagInfo {
  isUnregistered: boolean;
  /** The card/tag number as the reader reported it. Empty when the event carries none. */
  tagValue: string;
}

/**
 * Whether an event is an unregistered card read, and the tag value to show for it.
 *
 * Both readers send the number, in different shapes:
 *   ControlID  person_name = the raw card value, employee_no = the same
 *   Guarita    person_name = "" → event-processor rewrites it to "Tag não cadastrada" and sets
 *              not_registered_in_system, with employee_no = the decimal serial
 *
 * The UI used to detect this with /^\d{5,}$/ against person_name, which matches ControlID only
 * because its name happens to BE the number, and never matches Guarita, whose name is words.
 * Guarita card reads therefore showed "Tag não cadastrada" with nothing an operator could act
 * on — no number to look up, compare, or register.
 *
 * employee_no is the field that actually carries the value on both paths, so it wins. The digit
 * heuristic is kept only as a fallback for rows written before not_registered_in_system existed.
 */
export const getUnregisteredTagInfo = (event: AccessEvent): UnregisteredTagInfo => {
  const name = (event.person_name || '').trim();
  const employeeNo = ((event as any).employee_no || '').trim();

  // A vehicle read is identified by its plate, never by a bare tag number.
  const isVehicleEvent = !!event.vehicle_plate || event.event_type === 'lpr_access';

  // A resolved person is not an unregistered tag, however numeric their name looks.
  const resolvedToAPerson = !!event.person_id;

  const flagged = (event as any).not_registered_in_system === true;
  const looksLikeABareNumber = /^\d{5,}$/.test(name);

  const isUnregistered = !isVehicleEvent && !resolvedToAPerson && (flagged || looksLikeABareNumber);
  if (!isUnregistered) {
    return { isUnregistered: false, tagValue: '' };
  }

  // Prefer the explicit field; fall back to the name only when it is the number itself.
  // Never echo the placeholder back as if it were a tag.
  const candidate = employeeNo || name;
  const tagValue = candidate === UNREGISTERED_TAG_LABEL ? '' : candidate;

  return { isUnregistered: true, tagValue };
};

export interface EventTagInfo {
  /** The card/tag as the reader reported it. */
  tagValue: string;
  /** How to read tagValue — the two vendors use different encodings. */
  format: 'id' | 'dec';
}

/**
 * The physical tag presented on an access event, if any.
 *
 * The readers disagree about both the field AND the encoding, verified against production:
 *
 *   ControlID    event_type 'authentication_success'
 *                employee_no    = FNV hash of the person UUID — NOT a tag
 *                raw_event_data = {"card_value":"107374201641"} — the tag, already
 *                                 ControlID-encoded (fc<<32|cn)
 *
 *   NICE Guarita event_type 'rfid_access'
 *                employee_no    = "2011979" — the tag, as a plain decimal card number
 *                raw_event_data = null
 *
 * So employee_no is only trustworthy on rfid_access. On a ControlID event it holds a hash, and
 * rendering that as DEC/HEX/WG/ID would invite an operator to register a number no card has.
 * Returns null when the event carries no real tag, so callers can hide the affordance entirely.
 */
export const getEventTagInfo = (event: AccessEvent): EventTagInfo | null => {
  // A plate read identifies the vehicle; there is no card.
  if (event.event_type === 'lpr_access') return null;

  // 1. ControlID — the authoritative value, whatever the event type says.
  const raw = (event as any).raw_event_data;
  let parsed: any = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
  }
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    const cardValue = String(parsed.card_value ?? '').trim();
    // "0" is what a ControlID face reader sends when no card was involved.
    if (cardValue && cardValue !== '0') {
      return { tagValue: cardValue, format: 'id' };
    }
  }

  // 2. Guarita — employee_no, but ONLY on rfid_access. On any other event type it is a hash.
  if (event.event_type === 'rfid_access') {
    const employeeNo = ((event as any).employee_no || '').trim();
    if (employeeNo && employeeNo !== '0') {
      return { tagValue: employeeNo, format: 'dec' };
    }
  }

  return null;
};
